<script setup lang="ts">
import { debounce } from "lodash-es";
import { ref, watchEffect, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { baseInput, baseTextarea } from "@/common/components";

import { BreakPayloadType } from "@/common/types/breaks";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: BreakPayloadType | null;
    width?: string;
    patch?: boolean;
  }>(),
  {
    width: "100%",
    patch: false,
  }
);

const payload = ref<BreakPayloadType>({
  motive: "",
  description: null,
});

function update(key: keyof typeof payload.value) {
  if (props.patch) {
    emit("update-data", { [key]: payload.value[key] });
    return;
  }

  emit("update-data", {
    ...payload.value,
    [key]: payload.value[key],
  });
}

const debounceForm = debounce(update, 300);

watchEffect(() => {
  if (props.data) {
    const { motive, description } = props.data;
    payload.value = { motive, description };
  }
});

onMounted(() => {
  emit("update-data", payload.value);
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <base-input
        v-model="payload.motive"
        :value="payload.motive"
        :placeholder="t('analysis.placeholder.motive')"
        @input="debounceForm('motive')"
      >
        <template #label>
          {{ t("analysis.labels.motive") }}
          <sup>*</sup>
        </template>
      </base-input>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="debounceForm('description')"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>